<!-- NOTICE: KAPTION-INTEGRATION -->
<script setup>
import { ref, computed, watch } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import emojis from 'shared/components/emoji/emojisGroup.json';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  appTitle: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['update:modelValue']);

const isExpanded = ref(false);
const activeTab = ref('icon');
const search = ref('');
const fileInputRef = ref(null);
const isUploading = ref(false);

// Curated set of icons using Lucide icons (i-lucide-* classes)
const iconCategories = [
  {
    name: 'General',
    icons: [
      { name: 'layout-grid', class: 'i-lucide-layout-grid' },
      { name: 'settings', class: 'i-lucide-settings' },
      { name: 'home', class: 'i-lucide-home' },
      { name: 'star', class: 'i-lucide-star' },
      { name: 'heart', class: 'i-lucide-heart' },
      { name: 'bookmark', class: 'i-lucide-bookmark' },
      { name: 'folder', class: 'i-lucide-folder' },
      { name: 'file-text', class: 'i-lucide-file-text' },
      { name: 'image', class: 'i-lucide-image' },
      { name: 'video', class: 'i-lucide-video' },
      { name: 'music', class: 'i-lucide-music' },
    ],
  },
  {
    name: 'Communication',
    icons: [
      { name: 'message-circle', class: 'i-lucide-message-circle' },
      { name: 'mail', class: 'i-lucide-mail' },
      { name: 'phone', class: 'i-lucide-phone' },
      { name: 'megaphone', class: 'i-lucide-megaphone' },
      { name: 'user', class: 'i-lucide-user' },
      { name: 'users', class: 'i-lucide-users' },
      { name: 'message-square', class: 'i-lucide-message-square' },
      { name: 'messages-square', class: 'i-lucide-messages-square' },
    ],
  },
  {
    name: 'Business',
    icons: [
      { name: 'briefcase', class: 'i-lucide-briefcase' },
      { name: 'building', class: 'i-lucide-building' },
      { name: 'dollar-sign', class: 'i-lucide-dollar-sign' },
      { name: 'trending-up', class: 'i-lucide-trending-up' },
      { name: 'bar-chart', class: 'i-lucide-bar-chart' },
      { name: 'calendar', class: 'i-lucide-calendar' },
      { name: 'clipboard', class: 'i-lucide-clipboard' },
      { name: 'list-todo', class: 'i-lucide-list-todo' },
    ],
  },
  {
    name: 'Technology',
    icons: [
      { name: 'globe', class: 'i-lucide-globe' },
      { name: 'link', class: 'i-lucide-link' },
      { name: 'cloud', class: 'i-lucide-cloud' },
      { name: 'code', class: 'i-lucide-code' },
      { name: 'terminal', class: 'i-lucide-terminal' },
      { name: 'database', class: 'i-lucide-database' },
      { name: 'server', class: 'i-lucide-server' },
      { name: 'bot', class: 'i-lucide-bot' },
      { name: 'rocket', class: 'i-lucide-rocket' },
    ],
  },
  {
    name: 'Actions',
    icons: [
      { name: 'plus', class: 'i-lucide-plus' },
      { name: 'pencil', class: 'i-lucide-pencil' },
      { name: 'trash', class: 'i-lucide-trash' },
      { name: 'search', class: 'i-lucide-search' },
      { name: 'filter', class: 'i-lucide-filter' },
      { name: 'download', class: 'i-lucide-download' },
      { name: 'upload', class: 'i-lucide-upload' },
      { name: 'share', class: 'i-lucide-share' },
      { name: 'refresh-cw', class: 'i-lucide-refresh-cw' },
    ],
  },
];

const filteredIcons = computed(() => {
  if (!search.value) return iconCategories;

  const query = search.value.toLowerCase();
  return iconCategories
    .map(category => ({
      ...category,
      icons: category.icons.filter(icon => icon.name.includes(query)),
    }))
    .filter(category => category.icons.length > 0);
});

// Curated set of emojis for apps
const appEmojis = computed(() => {
  const popularEmojis = [
    '🤖',
    '📊',
    '📈',
    '💼',
    '🔧',
    '⚙️',
    '📱',
    '💻',
    '🌐',
    '📧',
    '📝',
    '📋',
    '✅',
    '⭐',
    '💡',
    '🔔',
    '🎯',
    '🚀',
    '💬',
    '📞',
    '🏠',
    '📁',
    '🔗',
    '🔍',
    '📅',
    '💰',
    '🎨',
    '🔒',
    '⚡',
    '🌟',
  ];

  if (!search.value) {
    return [
      {
        name: 'Popular',
        emojis: popularEmojis.map((emoji, i) => ({
          emoji,
          slug: `popular-${i}`,
        })),
      },
      ...emojis,
    ];
  }

  const query = search.value.toLowerCase();
  return emojis
    .map(category => ({
      ...category,
      emojis: category.emojis.filter(e =>
        e.slug.replaceAll('_', ' ').includes(query)
      ),
    }))
    .filter(category => category.emojis.length > 0);
});

const hasEmptyResult = computed(() => {
  if (activeTab.value === 'icon') {
    return filteredIcons.value.length === 0;
  }
  if (activeTab.value === 'emoji') {
    return appEmojis.value.every(cat => cat.emojis.length === 0);
  }
  return false;
});

const letterFallback = computed(() => {
  return props.appTitle ? props.appTitle.charAt(0).toUpperCase() : 'A';
});

const selectedIcon = computed(() => props.modelValue);

const selectionLabel = computed(() => {
  if (selectedIcon.value?.type === 'emoji') {
    return selectedIcon.value.value;
  }
  if (selectedIcon.value?.type === 'icon') {
    // Extract icon name from class (e.g., 'i-lucide-home' -> 'home')
    const iconClass = selectedIcon.value.value;
    const match = iconClass.match(/i-lucide-(.+)/);
    return match ? match[1] : 'Icon';
  }
  if (selectedIcon.value?.type === 'image') {
    return 'Custom image';
  }
  return 'Default (letter)';
});

const isIconSelected = (type, value) => {
  return (
    selectedIcon.value?.type === type && selectedIcon.value?.value === value
  );
};

const selectIcon = (type, value) => {
  emit('update:modelValue', { type, value });
};

const clearIcon = () => {
  emit('update:modelValue', {});
};

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
};

const triggerFileInput = () => {
  fileInputRef.value?.click();
};

const IMAGE_SIZE = 64;

const resizeImage = (file, maxSize = IMAGE_SIZE) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      // Set canvas to target size
      canvas.width = maxSize;
      canvas.height = maxSize;

      // Calculate scaling to cover the canvas (crop to square)
      const scale = Math.max(maxSize / img.width, maxSize / img.height);
      const scaledWidth = img.width * scale;
      const scaledHeight = img.height * scale;
      const offsetX = (maxSize - scaledWidth) / 2;
      const offsetY = (maxSize - scaledHeight) / 2;

      // Draw image centered and cropped
      ctx.drawImage(img, offsetX, offsetY, scaledWidth, scaledHeight);

      // Convert to base64 (PNG for quality)
      const base64 = canvas.toDataURL('image/png');
      resolve(base64);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load image from file
    const reader = new FileReader();
    reader.onload = e => {
      img.src = e.target?.result;
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
};

const handleFileUpload = async event => {
  const file = event.target.files?.[0];
  if (!file) return;

  // Validate file type - must be an image
  if (!file.type.startsWith('image/')) {
    return;
  }

  // Validate file size (max 1MB)
  const maxFileSize = 1 * 1024 * 1024;
  if (file.size > maxFileSize) {
    return;
  }

  isUploading.value = true;

  try {
    // Convert any image to 64x64 PNG
    const base64 = await resizeImage(file, IMAGE_SIZE);
    emit('update:modelValue', { type: 'image', value: base64 });
  } catch {
    // Silent fail
  } finally {
    isUploading.value = false;
  }

  // Reset input so same file can be selected again
  event.target.value = '';
};

watch(activeTab, () => {
  search.value = '';
});
</script>

<template>
  <div class="icon-gallery-picker">
    <label class="text-sm font-medium text-n-text-dark block mb-2">
      {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_LABEL') }}
    </label>

    <!-- Collapsed view - clickable to expand -->
    <button
      type="button"
      class="w-full flex items-center gap-3 p-2 border border-n-weak rounded-lg bg-n-slate-2 hover:bg-n-slate-3 transition-colors text-left"
      @click="toggleExpanded"
    >
      <!-- Current selection preview -->
      <div
        class="w-10 h-10 rounded-lg bg-n-slate-3 flex items-center justify-center text-xl border border-n-weak flex-shrink-0 overflow-hidden"
      >
        <span v-if="selectedIcon?.type === 'emoji'" class="text-xl">
          {{ selectedIcon.value }}
        </span>
        <Icon
          v-else-if="selectedIcon?.type === 'icon'"
          :icon="selectedIcon.value"
          class="text-n-slate-11 text-xl"
        />
        <img
          v-else-if="selectedIcon?.type === 'image'"
          :src="selectedIcon.value"
          alt="Custom icon"
          class="w-full h-full object-cover"
        />
        <span v-else class="text-lg font-semibold text-n-slate-9">
          {{ letterFallback }}
        </span>
      </div>

      <!-- Selection label -->
      <div class="flex-1 min-w-0">
        <span class="text-sm text-n-slate-12 truncate block">
          {{ selectionLabel }}
        </span>
        <span class="text-xs text-n-slate-10">
          {{
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_CLICK_TO_CHANGE')
          }}
        </span>
      </div>

      <!-- Chevron indicator -->
      <Icon
        :icon="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        class="text-n-slate-10 text-lg flex-shrink-0"
      />
    </button>

    <!-- Expanded picker panel -->
    <div
      v-if="isExpanded"
      class="mt-2 border border-n-weak rounded-lg bg-n-solid-1 p-3"
    >
      <!-- Tab buttons -->
      <div class="flex items-center gap-2 mb-3">
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-md transition-colors"
          :class="
            activeTab === 'icon'
              ? 'bg-n-brand text-white'
              : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
          "
          @click="activeTab = 'icon'"
        >
          {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_TAB_ICONS') }}
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-md transition-colors"
          :class="
            activeTab === 'emoji'
              ? 'bg-n-brand text-white'
              : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
          "
          @click="activeTab = 'emoji'"
        >
          {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_TAB_EMOJIS') }}
        </button>
        <button
          type="button"
          class="px-3 py-1.5 text-sm rounded-md transition-colors"
          :class="
            activeTab === 'upload'
              ? 'bg-n-brand text-white'
              : 'bg-n-slate-3 text-n-slate-11 hover:bg-n-slate-4'
          "
          @click="activeTab = 'upload'"
        >
          {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_TAB_UPLOAD') }}
        </button>
        <button
          v-if="selectedIcon?.type"
          type="button"
          class="px-3 py-1.5 text-sm rounded-md bg-n-ruby-3 text-n-ruby-11 hover:bg-n-ruby-4 transition-colors ml-auto"
          @click="clearIcon"
        >
          {{ $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_CLEAR') }}
        </button>
      </div>

      <!-- Search (only for icon/emoji tabs) -->
      <input
        v-if="activeTab !== 'upload'"
        v-model="search"
        type="text"
        class="w-full mb-3 !h-8 !text-sm"
        :placeholder="
          $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_SEARCH')
        "
      />

      <!-- Icon/Emoji grid -->
      <div
        v-if="activeTab !== 'upload'"
        class="h-[160px] overflow-y-auto border border-n-weak rounded-lg bg-n-slate-2 p-2"
      >
        <!-- Icons tab -->
        <template v-if="activeTab === 'icon'">
          <div
            v-for="category in filteredIcons"
            :key="category.name"
            class="mb-3"
          >
            <h5
              class="text-xs font-medium text-n-slate-11 mb-1.5 px-1 uppercase tracking-wide"
            >
              {{ category.name }}
            </h5>
            <div class="flex flex-wrap gap-1">
              <button
                v-for="icon in category.icons"
                :key="icon.name"
                type="button"
                class="w-8 h-8 rounded flex items-center justify-center transition-colors"
                :class="
                  isIconSelected('icon', icon.class)
                    ? 'bg-n-brand text-white'
                    : 'bg-n-background hover:bg-n-slate-4 text-n-slate-11'
                "
                :title="icon.name"
                @click="selectIcon('icon', icon.class)"
              >
                <Icon :icon="icon.class" class="text-lg" />
              </button>
            </div>
          </div>
        </template>

        <!-- Emojis tab -->
        <template v-if="activeTab === 'emoji'">
          <div v-for="category in appEmojis" :key="category.name" class="mb-3">
            <h5
              v-if="category.emojis.length > 0"
              class="text-xs font-medium text-n-slate-11 mb-1.5 px-1 uppercase tracking-wide"
            >
              {{ category.name }}
            </h5>
            <div v-if="category.emojis.length > 0" class="flex flex-wrap gap-1">
              <button
                v-for="item in category.emojis"
                :key="item.slug"
                type="button"
                class="w-8 h-8 rounded flex items-center justify-center text-lg transition-colors"
                :class="
                  isIconSelected('emoji', item.emoji)
                    ? 'bg-n-brand'
                    : 'bg-n-background hover:bg-n-slate-4'
                "
                @click="selectIcon('emoji', item.emoji)"
              >
                {{ item.emoji }}
              </button>
            </div>
          </div>
        </template>

        <!-- Empty state -->
        <div
          v-if="hasEmptyResult"
          class="flex flex-col items-center justify-center h-full text-n-slate-11"
        >
          <Icon icon="i-lucide-search-x" class="text-3xl mb-2" />
          <span class="text-sm">{{
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_NOT_FOUND')
          }}</span>
        </div>
      </div>

      <!-- Upload tab -->
      <div
        v-if="activeTab === 'upload'"
        class="border border-n-weak rounded-lg bg-n-slate-2 p-4"
      >
        <input
          ref="fileInputRef"
          type="file"
          accept="image/*"
          class="hidden"
          @change="handleFileUpload"
        />

        <!-- Current uploaded image preview -->
        <div
          v-if="selectedIcon?.type === 'image'"
          class="flex flex-col items-center gap-3 mb-4"
        >
          <div
            class="w-16 h-16 rounded-lg border border-n-weak overflow-hidden bg-white"
          >
            <img
              :src="selectedIcon.value"
              alt="Uploaded icon"
              class="w-full h-full object-cover"
            />
          </div>
          <span class="text-xs text-n-slate-11">{{
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_CURRENT_IMAGE')
          }}</span>
        </div>

        <!-- Upload button -->
        <button
          type="button"
          class="w-full flex flex-col items-center justify-center gap-2 py-6 border-2 border-dashed border-n-weak rounded-lg hover:border-n-brand hover:bg-n-slate-3 transition-colors"
          :disabled="isUploading"
          @click="triggerFileInput"
        >
          <Icon
            :icon="isUploading ? 'i-lucide-loader-2' : 'i-lucide-upload'"
            class="text-2xl text-n-slate-10"
            :class="isUploading ? 'animate-spin' : ''"
          />
          <span class="text-sm text-n-slate-11">
            {{
              isUploading
                ? $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_UPLOADING')
                : $t(
                    'INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_UPLOAD_CLICK'
                  )
            }}
          </span>
          <span class="text-xs text-n-slate-10">{{
            $t('INTEGRATION_SETTINGS.DASHBOARD_APPS.FORM.ICON_UPLOAD_HINT')
          }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.icon-gallery-picker input[type='text'] {
  @apply border border-n-weak rounded-md px-3 focus:ring-1 focus:ring-n-brand focus:border-n-brand;
}
</style>
